/**
 * useKnowledgeBase Hook
 *
 * Manages Knowledge Base state:
 * - Fetches files and quota on mount
 * - Handles file uploads
 * - Handles file deletions
 * - Polls for indexing status updates
 */

import { useCallback, useMemo, useState } from "react";
import type { QuotaInfo } from "@/types";
import {
  deleteKnowledgeBaseFile,
  type KnowledgeBaseData,
  type KnowledgeBaseFile,
  listKnowledgeBase,
  uploadKnowledgeBaseFile,
} from "@/features/knowledge-base/knowledge-base-client";
import { usePollingResource } from "@/features/internal/hooks/usePollingResource";
import { ApiError, getApiErrorMessage } from "@/services/api/errors";
import { isErrorCode } from "@vardic/shared/error-codes";
type TranslationFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

interface UseKnowledgeBaseResult {
  files: KnowledgeBaseFile[];
  quota: QuotaInfo | null;
  uploadFile: (file: File) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  isUploading: boolean;
  uploadProgress: number;
  refetch: () => Promise<void>;
}

const POLLING_INTERVAL = 30000; // 30 seconds
const MAX_POLL_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Localized message for a failed knowledge-base call: prefers the translation
 * keyed by the backend error code, then the backend prose, then `fallback`.
 */
function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return getApiErrorMessage(
      err.response ?? { message: err.message, code: err.code },
      fallback,
    );
  }
  return err instanceof Error ? err.message : fallback;
}

export function useKnowledgeBase(
  { t }: { t?: TranslationFunction } = {},
): UseKnowledgeBaseResult {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchFn = useCallback(async (): Promise<KnowledgeBaseData> => {
    try {
      return await listKnowledgeBase();
    } catch (err) {
      console.error("Failed to fetch knowledge base:", err);
      if (err instanceof ApiError || err instanceof Error) {
        throw err;
      }
      throw new Error(
        t?.("kb:errors.loadingFailed") ?? "Failed to fetch knowledge base",
      );
    }
  }, [t]);

  const {
    data,
    loading: isLoading,
    errorCode,
    errorMessage: pollingErrorMessage,
    refresh,
  } = usePollingResource(
    fetchFn,
    "knowledge-base",
    { intervalMs: POLLING_INTERVAL, maxDurationMs: MAX_POLL_DURATION_MS },
  );

  const files = data?.files ?? [];
  const quota = data?.quota ?? null;

  const error = useMemo(() => {
    if (actionError !== null) {
      return actionError;
    }
    if (errorCode) {
      return getApiErrorMessage(
        {
          code: isErrorCode(errorCode) ? errorCode : undefined,
          message: pollingErrorMessage ?? undefined,
        },
        t?.("kb:errors.loadingFailed") ?? "Failed to fetch knowledge base",
      );
    }
    return pollingErrorMessage;
  }, [actionError, errorCode, pollingErrorMessage, t]);

  const uploadFile = async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);
    setActionError(null);

    try {
      await uploadKnowledgeBaseFile(file);
      setUploadProgress(100);

      // Refresh the file list
      refresh();
    } catch (err) {
      setActionError(
        errorMessage(err, t?.("kb:errors.uploadFailed") ?? "Failed to upload file"),
      );
      throw err;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadFiles = async (files: File[]): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);
    setActionError(null);

    let uploadedFiles = 0;
    try {
      const totalFiles = files.length;

      // Upload files sequentially to track progress accurately
      for (const file of files) {
        try {
          await uploadKnowledgeBaseFile(file);
        } catch (err) {
          throw new Error(
            errorMessage(
              err,
              t?.("kb:errors.uploadFileFailed", { filename: file.name }) ??
                `Failed to upload ${file.name}`,
            ),
            { cause: err },
          );
        }

        uploadedFiles++;
        setUploadProgress(Math.round((uploadedFiles / totalFiles) * 100));
      }

      // Refresh the file list
      refresh();
    } catch (err) {
      setActionError(errorMessage(err, t?.("kb:errors.uploadFailed") ?? "Upload failed"));
      // The failed batch may have stored earlier files already; sync the
      // list and quota so they are visible instead of waiting for the next
      // background poll (users would otherwise re-upload and duplicate).
      if (uploadedFiles > 0) {
        refresh();
      }
      throw err;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteFile = async (fileId: string): Promise<void> => {
    setActionError(null);

    try {
      await deleteKnowledgeBaseFile(fileId);

      // Refresh the file list
      refresh();
    } catch (err) {
      setActionError(
        errorMessage(err, t?.("kb:errors.deleteFailed") ?? "Failed to delete file"),
      );
      throw err;
    }
  };

  const refetch = useCallback(async () => {
    refresh();
  }, [refresh]);

  return {
    files,
    quota,
    uploadFile,
    uploadFiles,
    deleteFile,
    isLoading,
    error,
    isUploading,
    uploadProgress,
    refetch,
  };
}
